<template>
  <a v-if="href"
    :href="href"
    :class="cls"
    @click="handleClick">
    <i v-if="icon"
      :class="icon" />
    <slot />
  </a>
  <button v-else
    :type="type || 'button'"
    :class="cls"
    :disabled="loading"
    @click="handleClick">
    <i v-if="loading"
      class="fa-solid fa-spinner fa-spin" />
    <template v-else>
      <i v-if="icon"
        :class="icon" />
      <template v-if="confirming">
        <slot name="confirm">{{ confirmText }}</slot>
      </template>
      <template v-else>
        <slot />
      </template>
    </template>
    <!-- confirm="question": asked in a popup with OK and Loobu; the modal teleports out of the button. -->
    <Modal v-if="popup"
      :open="dialogOpen"
      width="440rem"
      @close="dialogOpen = false">
      <p class="btn-confirm__text">{{ confirm }}</p>
      <template #footer>
        <div class="btn-confirm__actions">
          <button type="button"
            class="btn btn-secondary btn-confirm__btn"
            @click="dialogOpen = false">Loobu</button>
          <button type="button"
            :class="`btn btn-${variant} btn-confirm__btn`"
            @click="confirmed">OK</button>
        </div>
      </template>
    </Modal>
  </button>
</template>

<script>
export default {
  name: 'btn',
  emits: ['click'],
  props: {
    icon: String,
    variant: { type: String, default: 'primary' },
    href: String,
    type: String,
    className: { type: String, default: '' },
    confirm: { type: [Boolean, String], default: false },
    confirmText: { type: String, default: 'Oled kindel?' },
    confirmTimeout: { type: Number, default: 3000 },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      confirming: false,
      confirmTimer: null,
      dialogOpen: false
    }
  },
  computed: {
    // A text is the popup's question; plain confirm keeps the inline click-again.
    popup() {
      return typeof this.confirm === 'string' && this.confirm !== ''
    },
    cls() {
      return `btn btn-${this.variant} ${this.className}${this.confirming ? ' btn--confirming' : ''}`.trim()
    }
  },
  beforeUnmount() {
    clearTimeout(this.confirmTimer)
  },
  methods: {
    handleClick(e) {
      if (!this.confirm) {
        this.$emit('click', e)
        return
      }
      if (this.popup) {
        this.dialogOpen = true
        return
      }
      if (this.confirming) {
        clearTimeout(this.confirmTimer)
        this.confirming = false
        this.$emit('click', e)
        return
      }
      this.confirming = true
      this.confirmTimer = setTimeout(() => {
        this.confirming = false
      }, this.confirmTimeout)
    },
    // The modal steps back over its own history entry as it closes; the action waits for that, so a navigation it
    // starts stays. The timer covers a close that does not step back (the modal skips it after a navigation).
    confirmed() {
      let done = false
      const fire = () => {
        if (done) return
        done = true
        window.removeEventListener('popstate', fire)
        this.$emit('click')
      }
      window.addEventListener('popstate', fire)
      setTimeout(fire, 300)
      this.dialogOpen = false
    }
  }
}
</script>

<style lang="scss">
.btn-confirm__text {
  margin: 0;
  // Clear of the modal's close button in the corner.
  padding-right: 36rem;
  font-size: 15rem;
  font-weight: 400;
  // A \n in the question starts a new line.
  white-space: pre-line;
}

.btn-confirm__actions {
  display: flex;
  justify-content: center;
  gap: 12rem;
}

// Between .btn--sm and the full size, the two the same width.
.btn.btn-confirm__btn {
  justify-content: center;
  min-width: 120rem;
  height: 42rem;
  padding: 0 24rem;
  font-size: 14rem;
}

// Heights match Field's md / sm; set here, or gentelella's .btn { height: 32px } wins.
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6rem;
  height: 48rem;
  padding: 0 36rem;
  font-size: 16rem;
  font-weight: 600;
  font-family: inherit;
  border-radius: 100rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 2rem solid transparent;

  i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 2rem;
  }
}

.btn--sm {
  height: 38rem;
  padding: 0 14rem;
  font-size: 12rem;
  border-radius: 20rem;
  border-width: 1rem;
}

// Compact row and card actions; not for buttons beside a Field.
.btn--xs {
  gap: 4rem;
  height: 28rem;
  padding: 0 10rem;
  font-size: 11rem;
  border-radius: 20rem;
  border-width: 1rem;
}

.btn-primary {
  background: var(--accent);
  color: #fff;
  border-color: var(--accent);

  &:hover {
    background: var(--accent-hover);
    border-color: var(--accent-hover);
    transform: translateY(-2rem);
    box-shadow: 0 4rem 16rem rgba(0, 174, 239, 0.35);
  }
}

.btn-outline {
  background: transparent;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.6);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: #fff;
    transform: translateY(-2rem);
  }
}

.btn-secondary {
  background: #fff;
  color: #555;
  border-color: var(--border-color);

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.btn-danger {
  background: #fff;
  color: #d44;
  border-color: var(--border-color);

  &:hover {
    border-color: #d44;
    color: #d44;
    background: #fef0f0;
  }
}

.btn--confirming {
  animation: btn-flash 0.4s ease;
}

@keyframes btn-flash {
  0%,
  100% {
    opacity: 1;
  }
  25% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  75% {
    opacity: 0.3;
  }
}
</style>
